import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { LuCheck, LuChevronDown, LuGauge } from "react-icons/lu";
import "./SpeedControl.scss";

import {
  SPEED_PRESETS,
  YOUTUBE_MAX_SPEED,
  formatSpeed,
} from "../../../../../../common/preview_speed";

// Re-exported so existing imports keep working; the Visor player reads the same
// values straight from common/ (it must not import _editor/ code).
export { SPEED_PRESETS, YOUTUBE_MAX_SPEED, formatSpeed };

/**
 * Playback-speed split button for the timeline transport bar.
 */
function SpeedControl({ speed = 1, onChange, disabled, maxSpeed, limitReason }) {
  const t = (key) => i18n.t(`messages.previewSpeed.${key}`);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const groupRef = useRef(null);
  const menuBtnRef = useRef(null);
  const menuRef = useRef(null);
  const fast = speed !== 1;

  const allowed = SPEED_PRESETS.filter((p) => typeof maxSpeed !== "number" || p <= maxSpeed);
  const nextSpeed = allowed.find((p) => p > speed) ?? allowed[0];

  const cycleSpeed = () => {
    if (nextSpeed == null || nextSpeed === speed) return;
    onChange?.(nextSpeed);
  };

  const openMenu = () => {
    const rect = groupRef.current?.getBoundingClientRect();
    if (!rect) return;
    setAnchor({ left: rect.left, bottom: window.innerHeight - rect.top + 6 });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (groupRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        menuBtnRef.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const items = Array.from(
        menuRef.current?.querySelectorAll(".it-speed__item:not(:disabled)") || []
      );
      if (!items.length) return;
      const from = items.indexOf(document.activeElement);
      const step = e.key === "ArrowDown" ? 1 : -1;
      const next = from === -1 ? (step === 1 ? 0 : items.length - 1) : from + step;
      items[(next + items.length) % items.length].focus();
    };
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const groupClass = [
    "it-speed",
    fast && "it-speed--fast",
    open && "it-speed--open",
    disabled && "it-speed--disabled",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div ref={groupRef} className={groupClass}>
        <button
          type="button"
          className="it-speed-btn it-speed-btn--cycle"
          onClick={cycleSpeed}
          disabled={disabled}
          title={`${t("cycleTitle")}${nextSpeed != null ? ` (${formatSpeed(nextSpeed)})` : ""}`}
          aria-label={t("cycleTitle")}
        >
          <LuGauge size={15} />
        </button>
        <button
          ref={menuBtnRef}
          type="button"
          className="it-speed-btn it-speed-btn--menu"
          onClick={() => (open ? setOpen(false) : openMenu())}
          disabled={disabled}
          title={t("buttonTitle")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className="it-speed-btn__value">{formatSpeed(speed)}</span>
          <LuChevronDown className="it-speed-btn__caret" size={13} />
        </button>
      </div>

      {open && anchor && (
        <div
          ref={menuRef}
          className="it-speed__menu"
          role="menu"
          style={{ left: anchor.left, bottom: anchor.bottom }}
        >
          <div className="it-speed__title">{t("title")}</div>
          {SPEED_PRESETS.map((preset) => {
            const locked = typeof maxSpeed === "number" && preset > maxSpeed;
            return (
              <button
                key={preset}
                type="button"
                role="menuitemradio"
                aria-checked={preset === speed}
                disabled={locked}
                title={locked ? limitReason : undefined}
                className={`it-speed__item${preset === speed ? " it-speed__item--active" : ""}`}
                onClick={() => {
                  onChange?.(preset);
                  setOpen(false);
                  menuBtnRef.current?.focus();
                }}
              >
                <span className="it-speed__item-label">{formatSpeed(preset)}</span>
                {preset === 1 && <span className="it-speed__item-hint">{t("normal")}</span>}
                {preset === speed && <LuCheck className="it-speed__item-check" size={14} />}
              </button>
            );
          })}
          <div className="it-speed__footer">
            {limitReason && <div className="it-speed__limit">{limitReason}</div>}
            <div className="it-speed__note">{t("note")}</div>
          </div>
        </div>
      )}
    </>
  );
}

export default SpeedControl;
